/**
 * Markdown → HTML for the reading view.
 *
 * Written here rather than pulled in, for one reason: §22 and §30 require that
 * nothing in a document ever executes, and the safest way to guarantee that is
 * a renderer that has no path from input to raw HTML at all. Every character of
 * the source is escaped before any markup is added, so a `<script>` in the
 * document is text, not an element — there is nothing for a sanitiser to miss.
 *
 * Raw HTML in the source is therefore shown as literal text. That is a
 * deliberate limitation, not an oversight, and the source view is one click
 * away for anyone who needs to see what was written.
 */

function esc(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Only http(s) and mailto survive; everything else becomes plain text (§30). */
function safeUrl(url: string): string | null {
  const trimmed = url.trim();
  if (/^(https?:|mailto:)/i.test(trimmed)) return esc(trimmed);
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return null; // some other scheme
  return esc(trimmed); // relative
}

function inline(source: string): string {
  let out = esc(source);

  // Code spans first: their contents must not be re-interpreted.
  const codes: string[] = [];
  out = out.replace(/`([^`]+)`/g, (_, code: string) => {
    codes.push(code);
    return ` ${codes.length - 1} `;
  });

  out = out
    .replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (whole, alt: string, url: string) => {
      const href = safeUrl(url);
      return href ? `<img src="${href}" alt="${alt}">` : whole;
    })
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (whole, label: string, url: string) => {
      const href = safeUrl(url);
      return href
        ? `<a href="${href}" target="_blank" rel="noopener noreferrer nofollow">${label}</a>`
        : whole;
    })
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>')
    .replace(/~~([^~]+)~~/g, '<s>$1</s>');

  return out.replace(/ (\d+) /g, (_, i: string) => `<code>${codes[Number(i)]}</code>`);
}

export function renderMarkdown(source: string): string {
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const out: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i]!;

    if (/^```/.test(line)) {
      const buffer: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i]!)) buffer.push(lines[i++]!);
      i++;
      out.push(`<pre><code>${esc(buffer.join('\n'))}</code></pre>`);
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1]!.length;
      out.push(`<h${level}>${inline(heading[2]!)}</h${level}>`);
      i++;
      continue;
    }

    if (/^\s*([-*_])\s*\1\s*\1[\s-*_]*$/.test(line)) {
      out.push('<hr>');
      i++;
      continue;
    }

    // GitHub-style table: a header row followed by a delimiter row.
    if (/^\s*\|.*\|\s*$/.test(line) && /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1] ?? '')) {
      const cells = (row: string): string[] =>
        row.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
      const head = cells(line);
      i += 2;
      const body: string[][] = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i]!)) body.push(cells(lines[i++]!));
      out.push(
        `<table><thead><tr>${head.map((h) => `<th>${inline(h)}</th>`).join('')}</tr></thead>`
        + `<tbody>${body.map((r) => `<tr>${r.map((c) => `<td>${inline(c)}</td>`).join('')}</tr>`).join('')}</tbody></table>`,
      );
      continue;
    }

    if (/^\s*>/.test(line)) {
      const buffer: string[] = [];
      while (i < lines.length && /^\s*>/.test(lines[i]!)) buffer.push(lines[i++]!.replace(/^\s*>\s?/, ''));
      out.push(`<blockquote>${renderMarkdown(buffer.join('\n'))}</blockquote>`);
      continue;
    }

    if (/^\s*([-*+]|\d+\.)\s+/.test(line)) {
      const ordered = /^\s*\d+\./.test(line);
      const items: string[] = [];
      while (i < lines.length && /^\s*([-*+]|\d+\.)\s+/.test(lines[i]!)) {
        items.push(lines[i++]!.replace(/^\s*([-*+]|\d+\.)\s+/, ''));
      }
      const tag = ordered ? 'ol' : 'ul';
      out.push(`<${tag}>${items.map((it) => `<li>${inline(it)}</li>`).join('')}</${tag}>`);
      continue;
    }

    if (!line.trim()) { i++; continue; }

    const paragraph: string[] = [];
    while (
      i < lines.length && lines[i]!.trim()
      && !/^(#{1,6}\s|```|\s*>|\s*([-*+]|\d+\.)\s)/.test(lines[i]!)
    ) {
      paragraph.push(lines[i++]!);
    }
    out.push(`<p>${inline(paragraph.join('\n'))}</p>`);
  }

  return out.join('\n');
}
